// LE PANNEAU ASSISTANT DE L'ATELIER — une IA qui aide à réfléchir, sans ranger.
//
// Deux usages ici (le troisième, « préparer l'insight d'un groupe », vit dans
// le formulaire d'insight du groupe) :
//   · REGROUPER   — des regroupements possibles des post-it libres. « Voir »
//                   les sélectionne sur le plan ; « Appliquer » les rapproche
//                   et en fait un groupe ; « Écarter » les oublie ;
//   · QUESTIONNER — une question sur le plan ; la réponse cite les post-it sur
//                   lesquels elle s'appuie, et un clic les montre.
//
// Rien n'est écrit tant que la personne n'a pas cliqué « Appliquer ».
import { useId, useRef, useState, type FormEvent } from 'react';
import { useBoardAsk, useBoardClusters } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { BoardClusterProposal } from '../api/types';
import { Button, ErrorBanner, IconButton } from '../components/ui';
import { Icon } from '../components/icons';
import './assistant.css';

/** Des questions qu'on se pose en séance de synthèse — un clic les pose. */
const STARTERS = [
  'Qu’est-ce qui se contredit sur ce plan ?',
  'Quel profil d’utilisateur n’apparaît presque pas ?',
  'Quels post-it restent isolés, et pourquoi ?',
  'Qu’est-ce qui manque pour conclure ?',
];

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'L’assistant n’a pas pu répondre.';
}

const plural = (n: number, one: string) => `${n} ${one}${n > 1 ? 's' : ''}`;

export function AssistantPanel({
  projectId, studyId, excerpt, onFocus, onApply, onClose,
}: {
  projectId: string;
  studyId: string | null;
  /** Le début du texte d'un post-it, pour citer sans tout recopier. */
  excerpt: (noteId: string) => string | null;
  /** Sélectionner ces post-it et les cadrer à l'écran. */
  onFocus: (noteIds: string[]) => void;
  /** Rapprocher ces post-it et en faire un groupe. */
  onApply: (proposal: BoardClusterProposal) => Promise<void>;
  onClose: () => void;
}) {
  const clusters = useBoardClusters(projectId);
  const ask = useBoardAsk(projectId);
  const [proposals, setProposals] = useState<BoardClusterProposal[] | null>(null);
  const [applying, setApplying] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const questionId = useId();

  const propose = () => {
    clusters.mutate(studyId, { onSuccess: (r) => setProposals(r.proposals) });
  };
  // Un seul regroupement appliqué à la fois : un double-clic créerait deux
  // groupes (l'état `applying` n'est pas encore à jour au second clic).
  const busy = useRef(false);
  const apply = async (p: BoardClusterProposal) => {
    if (busy.current) return;
    busy.current = true;
    setApplying(p.key);
    try {
      await onApply(p);
      setProposals((list) => (list ?? []).filter((x) => x.key !== p.key));
    } finally {
      busy.current = false;
      setApplying(null);
    }
  };
  const submit = (e?: FormEvent, q = question) => {
    e?.preventDefault();
    const text = q.trim();
    if (text.length < 3) return;
    setQuestion(text);
    ask.mutate({ question: text, studyId });
  };

  return (
    <aside className="board-ui board-assistant" aria-label="Assistant IA">
      <div className="assistant-head">
        <span className="assistant-title"><Icon name="sparkles" size={14} /> Assistant</span>
        <IconButton icon="close" label="Fermer l’assistant" onClick={onClose} />
      </div>
      <p className="assistant-note">
        Il propose, vous décidez : rien ne change sur le plan tant que vous n’avez pas appliqué.
      </p>

      {/* — Regrouper — */}
      <section className="assistant-block">
        <h3 className="assistant-h">Regrouper</h3>
        <p className="assistant-hint">Des regroupements possibles des post-it qui ne sont dans aucun groupe.</p>
        <Button variant="ghost" onClick={propose} loading={clusters.isPending}>
          {proposals ? 'Proposer à nouveau' : 'Proposer des regroupements'}
        </Button>
        {clusters.error ? <ErrorBanner>{errorMessage(clusters.error)}</ErrorBanner> : null}
        {clusters.data?.mode === 'fake' ? <p className="assistant-mode">Démonstration locale, sans modèle.</p> : null}
        {proposals && proposals.length === 0 ? (
          <p className="assistant-hint">Rien de net à proposer : les post-it libres ne se ressemblent pas assez.</p>
        ) : null}
        {proposals && proposals.length > 0 ? (
          <ul className="assistant-list">
            {proposals.map((p) => (
              <li key={p.key} className="assistant-card">
                <p className="assistant-card-title">{p.name}</p>
                {p.rationale ? <p className="assistant-hint">{p.rationale}</p> : null}
                <div className="assistant-actions">
                  <Button variant="link" size="s" type="button" onClick={() => onFocus(p.noteIds)}>
                    Voir les {plural(p.noteIds.length, 'post-it')}
                  </Button>
                  <Button variant="primary" loading={applying === p.key} onClick={() => void apply(p)}>Appliquer</Button>
                  <Button variant="ghost" onClick={() => setProposals((l) => (l ?? []).filter((x) => x.key !== p.key))}>Écarter</Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {/* — Questionner — */}
      <section className="assistant-block">
        <h3 className="assistant-h">Interroger le plan</h3>
        <form onSubmit={submit} className="assistant-ask">
          <label htmlFor={questionId} className="sr-only">Votre question sur le plan</label>
          <textarea
            id={questionId}
            className="zy-input"
            rows={2}
            value={question}
            placeholder="Ex. : qu’est-ce qui contredit le groupe sur la synchro ?"
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          />
          <Button type="submit" variant="ghost" loading={ask.isPending} disabled={question.trim().length < 3}>Demander</Button>
        </form>
        <div className="assistant-starters">
          {STARTERS.map((s) => (
            <button key={s} type="button" className="assistant-chip" onClick={() => submit(undefined, s)} disabled={ask.isPending}>{s}</button>
          ))}
        </div>
        {ask.error ? <ErrorBanner>{errorMessage(ask.error)}</ErrorBanner> : null}
        {ask.data ? (
          <div className="assistant-answer">
            <p>{ask.data.answer}</p>
            {ask.data.noteIds.length > 0 ? (
              <>
                <Button variant="link" size="s" type="button" onClick={() => onFocus(ask.data.noteIds)}>
                  Montrer les {plural(ask.data.noteIds.length, 'post-it')} cités
                </Button>
                <ul className="assistant-cites">
                  {ask.data.noteIds.map((id) => (
                    <li key={id}>
                      <button type="button" onClick={() => onFocus([id])}>{excerpt(id) ?? 'Post-it retiré du plan'}</button>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {ask.data.mode === 'fake' ? <p className="assistant-mode">Démonstration locale, sans modèle.</p> : null}
          </div>
        ) : null}
      </section>
    </aside>
  );
}
